import { useState } from "react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/text-input";
import { Badge } from "@/components/ui/badge";
import { Icons } from "@/components/ui/icons";

/**
 * Workshop: Profile section for Settings modal.
 * Avatar, name, email, timezone, password, save, and danger zone.
 */

/* ---------- Mock data ---------- */

const MOCK_PROFILE = {
  name: "Alex Chen",
  email: "alex@denker.ai",
  emailVerified: true,
  timezone: "Europe/Berlin",
  avatarLetter: "A",
  avatarColor: "#818CF8",
};

const TIMEZONES = [
  "America/New_York",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Berlin",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Australia/Sydney",
];

/* ---------- Props ---------- */

interface ProfileSectionProps {
  className?: string;
  "data-testid"?: string;
}

/* ---------- Component ---------- */

export function ProfileSection({
  className,
  "data-testid": testId = "settings-profile",
}: ProfileSectionProps) {
  const [name, setName] = useState(MOCK_PROFILE.name);
  const [email, setEmail] = useState(MOCK_PROFILE.email);
  const [timezone, setTimezone] = useState(MOCK_PROFILE.timezone);
  const [showPassword, setShowPassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  return (
    <div className={cn("space-y-6", className)} data-testid={testId}>
      {/* Avatar */}
      <div
        className="flex items-center gap-4"
        data-testid="profile-avatar-section"
      >
        <div
          className="flex h-16 w-16 items-center justify-center rounded-full text-xl font-bold text-white"
          style={{ backgroundColor: MOCK_PROFILE.avatarColor }}
          data-testid="profile-avatar"
        >
          {MOCK_PROFILE.avatarLetter}
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            data-testid="profile-change-avatar"
          >
            <Icons.Upload className="h-3.5 w-3.5" />
            Change
          </Button>
          <Button variant="ghost" size="sm" data-testid="profile-remove-avatar">
            Remove
          </Button>
        </div>
      </div>

      {/* Name */}
      <FieldGroup label="Name">
        <TextInput
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="profile-name-input"
        />
      </FieldGroup>

      {/* Email */}
      <FieldGroup label="Email">
        <div className="flex items-center gap-2">
          <TextInput
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="profile-email-input"
          />
          <Badge
            variant={
              MOCK_PROFILE.emailVerified ? "status-success" : "status-error"
            }
            data-testid="profile-email-badge"
          >
            {MOCK_PROFILE.emailVerified ? "Verified" : "Unverified"}
          </Badge>
        </div>
      </FieldGroup>

      {/* Timezone */}
      <FieldGroup label="Timezone">
        <select
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          className="h-[34px] w-full rounded-lg border border-glass-stroke-light bg-glass-fill-heavy px-3 text-[10px] text-primary backdrop-blur-glass-sm focus:border-accent focus:outline-none"
          data-testid="profile-timezone-select"
        >
          {TIMEZONES.map((tz) => (
            <option key={tz} value={tz}>
              {tz}
            </option>
          ))}
        </select>
      </FieldGroup>

      {/* Password */}
      {!showPassword ? (
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-medium text-secondary">
            Password
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowPassword(true)}
            data-testid="profile-change-password-btn"
          >
            Change password
          </Button>
        </div>
      ) : (
        <FieldGroup label="Password">
          <div className="space-y-2">
            <TextInput
              type="password"
              placeholder="Current password"
              data-testid="profile-current-password"
            />
            <TextInput
              type="password"
              placeholder="New password"
              data-testid="profile-new-password"
            />
            <TextInput
              type="password"
              placeholder="Confirm new password"
              data-testid="profile-confirm-password"
            />
            <div className="flex gap-2">
              <Button size="sm" data-testid="profile-save-password">
                Save
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPassword(false)}
                data-testid="profile-cancel-password"
              >
                Cancel
              </Button>
            </div>
          </div>
        </FieldGroup>
      )}

      {/* Save */}
      <div className="flex justify-end">
        <Button size="sm" data-testid="profile-save-btn">
          Save changes
        </Button>
      </div>

      {/* Danger zone */}
      <div className="border-t border-glass-stroke-subtle pt-4">
        <h3
          className="mb-2 text-[11px] font-semibold text-danger"
          data-testid="profile-danger-heading"
        >
          Danger Zone
        </h3>
        {!showDeleteConfirm ? (
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setShowDeleteConfirm(true)}
            data-testid="profile-delete-account-btn"
          >
            <Icons.Trash2 className="h-3.5 w-3.5" />
            Delete account
          </Button>
        ) : (
          <div className="space-y-2 rounded-lg border border-danger/30 bg-danger/5 p-3">
            <p className="text-[10px] text-danger">
              This action is irreversible. All your data will be permanently
              deleted.
            </p>
            <div className="flex gap-2">
              <Button
                variant="destructive"
                size="sm"
                data-testid="profile-confirm-delete"
              >
                Yes, delete my account
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDeleteConfirm(false)}
                data-testid="profile-cancel-delete"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Helper ---------- */

function FieldGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className="text-[10px] font-medium text-secondary">{label}</label>
      {children}
    </div>
  );
}

export type { ProfileSectionProps };
